import { Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { ReactNode, useState } from 'react';
import PageLoader from 'src/UI/loader/PageLoader';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useFamilyMembers } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { GroupMember } from '../grouping';
import useExpenditures from '../hooks/useExpenditures';
import useIncomes from '../hooks/useIncomes';
import useLifeEvents from '../hooks/useLifeEvents';
import { JointCashflowModals } from '../joint/JointCashflowModals';
import {
  buildJointExpenditureRows,
  buildJointIncomeRows,
  buildJointLifeEventRows,
} from '../joint/owners';
import { ExpenditureRow, IncomeRow, LifeEventRow } from '../rows';
import { Period } from '../utils';
import { CashflowsLayout } from './CashflowsLayout';
import { memberIndex, toGroupMembers } from './useCashflowMembers';
import { DataLoadError } from 'src/common/components/data-states/DataLoadError';
import { NoRecordedDataCallout } from 'src/common/components/data-states/NoRecordedDataCallout';
import { TabShowsItsOwnEmptyState } from 'src/common/components/data-states/TabShowsItsOwnEmptyState';
import { CASHFLOWS_SIESTA_PROMPT, failedCashflowsLabel } from './emptyCashflowsState';

/**
 * A client's read returns every item they hold, each whole and with everyone on it, so a row is
 * the whole item and the form can rewrite its owners or delete it outright. The section totals
 * count only this client's part of a shared item, as their cashflow model does.
 */
export function ClientCashflowsRedesign({ modelsSection }: { modelsSection: ReactNode }) {
  const { clientId, client } = useCurrentClient();
  const {
    isLoading: loadingIncomes,
    isError: incomesErrored,
    isLoadingError: incomesFailed,
    refetch: refetchIncomes,
    incomesData,
  } = useIncomes();
  const {
    isLoading: loadingExpenditures,
    isError: expendituresErrored,
    isLoadingError: expendituresFailed,
    refetch: refetchExpenditures,
    expendituresData,
  } = useExpenditures();
  const {
    isLoading: loadingLifeEvents,
    isError: lifeEventsErrored,
    isLoadingError: lifeEventsFailed,
    refetch: refetchLifeEvents,
    lifeEventsData,
  } = useLifeEvents();
  const { members: familyMembers, isLoading: loadingFamilyMembers } = useFamilyMembers();

  const [period, setPeriod] = useState<Period>('monthly');
  const [incomeOpen, income] = useDisclosure(false);
  const [expenditureOpen, expenditure] = useDisclosure(false);
  const [lifeEventOpen, lifeEvent] = useDisclosure(false);
  const [editedIncome, setEditedIncome] = useState<IncomeRow | undefined>();
  const [editedExpenditure, setEditedExpenditure] = useState<ExpenditureRow | undefined>();
  const [editedLifeEvent, setEditedLifeEvent] = useState<LifeEventRow | undefined>();

  const self: GroupMember = {
    clientId,
    firstName: client?.firstName ?? '',
    lastName: client?.lastName ?? '',
  };
  // A client with no family can still record cashflows — against themselves alone.
  const pickableMembers = familyMembers.length > 0 ? toGroupMembers(familyMembers) : [self];
  const byClientId = memberIndex(
    pickableMembers.some(member => member.clientId === clientId)
      ? pickableMembers
      : [self, ...pickableMembers],
  );

  if (loadingIncomes || loadingExpenditures || loadingLifeEvents || loadingFamilyMembers) {
    return <PageLoader absolute={false} />;
  }

  const retryAll = () =>
    Promise.all([refetchIncomes(), refetchExpenditures(), refetchLifeEvents()]);

  // A read that never loaded is not a client with nothing recorded. Totalling what did arrive
  // would put a figure on the tab that is not theirs, and strike a net position against it. A
  // refetch that fails over rows already on screen is reported above them instead: replacing the
  // tab under the adviser would unmount whatever form they have open, losing what they had typed.
  if (incomesFailed || expendituresFailed || lifeEventsFailed) {
    return (
      <Stack gap="md">
        <DataLoadError
          title="Cashflows could not be loaded"
          testId="cashflows-load-error"
          subject={`${client?.firstName ?? 'this client'}'s`}
          what={failedCashflowsLabel({
            incomes: incomesFailed,
            expenditures: expendituresFailed,
            lifeEvents: lifeEventsFailed,
          })}
          onRetry={retryAll}
        />
        {modelsSection}
      </Stack>
    );
  }

  const hasAnyCashflows =
    (incomesData?.length ?? 0) + (expendituresData?.length ?? 0) + (lifeEventsData?.length ?? 0) >
    0;

  // The reads all loaded once, so an error now is a refetch that failed over rows still on screen.
  // They stay, but may be out of date, so the adviser is told which ones.
  const refetchFailure =
    incomesErrored || expendituresErrored || lifeEventsErrored ? (
      <DataLoadError
        title="Cashflows could not be refreshed"
        testId="cashflows-refetch-error"
        subject={`${client?.firstName ?? 'this client'}'s latest`}
        what={failedCashflowsLabel({
          incomes: incomesErrored,
          expenditures: expendituresErrored,
          lifeEvents: lifeEventsErrored,
        })}
        onRetry={retryAll}
      />
    ) : null;

  return (
    <>
      <CashflowsLayout
        period={period}
        onPeriodChange={setPeriod}
        incomeRows={buildJointIncomeRows(incomesData ?? [], byClientId, { clientId })}
        expenditureRows={buildJointExpenditureRows(expendituresData ?? [], byClientId, {
          clientId,
        })}
        lifeEventRows={buildJointLifeEventRows(lifeEventsData ?? [], byClientId, { clientId })}
        banner={
          <>
            {refetchFailure}
            {!hasAnyCashflows && (
              <NoRecordedDataCallout
                title="No cashflows recorded yet"
                subjects={[{ clientId, firstName: client?.firstName }]}
                siestaPrompt={CASHFLOWS_SIESTA_PROMPT}
                testId="cashflows-empty-state"
              >
                Nothing is recorded for {client?.firstName ?? 'this client'} yet. Process their
                documents or ask Siesta to pull in their income, expenditure and future life events
                — or add an item by hand from any section below.
              </NoRecordedDataCallout>
            )}
          </>
        }
        incomeActions={{
          onAdd: () => {
            setEditedIncome(undefined);
            income.open();
          },
          onRowClick: row => {
            setEditedIncome(row);
            income.open();
          },
        }}
        expenditureActions={{
          onAdd: () => {
            setEditedExpenditure(undefined);
            expenditure.open();
          },
          onRowClick: row => {
            setEditedExpenditure(row);
            expenditure.open();
          },
        }}
        lifeEventActions={{
          onAdd: () => {
            setEditedLifeEvent(undefined);
            lifeEvent.open();
          },
          onRowClick: row => {
            setEditedLifeEvent(row);
            lifeEvent.open();
          },
        }}
        modelsSection={
          <TabShowsItsOwnEmptyState value={!hasAnyCashflows}>
            {modelsSection}
          </TabShowsItsOwnEmptyState>
        }
      />

      <JointCashflowModals
        members={pickableMembers}
        defaultClientId={clientId}
        viewingClientId={clientId}
        income={{
          opened: incomeOpen,
          row: editedIncome,
          onClose: () => {
            income.close();
            setEditedIncome(undefined);
          },
        }}
        expenditure={{
          opened: expenditureOpen,
          row: editedExpenditure,
          onClose: () => {
            expenditure.close();
            setEditedExpenditure(undefined);
          },
        }}
        lifeEvent={{
          opened: lifeEventOpen,
          row: editedLifeEvent,
          onClose: () => {
            lifeEvent.close();
            setEditedLifeEvent(undefined);
          },
        }}
      />
    </>
  );
}
